import React, { useCallback, useMemo } from 'react'
import {
  AnyActionType,
  SetVisibilityAction,
  ISetVisibilityAction,
  IActionComponentProps,
} from '~core/forge.type'
import useDispatch from '~editor/hooks/useDispatch'
import { HStack } from '@chakra-ui/react'
import { MultiSelect, Select } from '~editor/components/Select'
import { Box } from '@chakra-ui/react'
import { useSelector } from 'react-redux'
import {
  getNodesWithoutInternals,
  getSelectedNode,
} from '~editor/selectors/nodes'

interface IProps extends IActionComponentProps {
  action: SetVisibilityAction
}

interface IOption {
  label: string
  value: ISetVisibilityAction['option']
}

const OPTIONS: IOption[] = [
  {
    label: 'Toggle',
    value: 'toggle',
  },
  {
    label: 'Show',
    value: 'show',
  },
  {
    label: 'Hide',
    value: 'hide',
  },
]

export const SetVisibilityActionComponent = ({
  action,
  actions,
  activeActionGesture,
  isDisabled,
}: IProps) => {
  const dispatch = useDispatch()
  const node = useSelector(getSelectedNode)
  const nodesWithouthInternals = useSelector(getNodesWithoutInternals)
  const nodesOptions = useMemo(() => {
    return (
      Object.values(nodesWithouthInternals)
        // .filter((_node) => _node.id !== node.id)
        .map((_node) => {
          let label = _node.name || _node.type

          if (_node.id === node.id) {
            label = `[Current] ${label}`
          }

          return {
            label,
            value: _node.id,
          }
        })
    )
  }, [nodesWithouthInternals])

  const nodesIDs = useMemo(() => {
    if (!action.nodeIDs) return []
    return action.nodeIDs
      .map((nodeID) => {
        return nodesOptions.find((nodeOption) => {
          return nodeOption.value === nodeID
        })
      })
      .filter((nodeOption) => nodeOption)
  }, [action.nodeIDs, nodesOptions])

  const optionValue = useMemo(() => {
    return (
      OPTIONS.find((option) => action.option === option.value) || OPTIONS[0]
    )
  }, [action.option])

  const updateNodeIDs = useCallback(
    (values: any[]) => {
      const _nodeIDs = values.map(({ value }) => value)
      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: actions.map((_a: AnyActionType) => {
          const _action = { ..._a } as SetVisibilityAction

          if (action.id === _action.id) {
            _action.nodeIDs = _nodeIDs
          }

          return _action
        }),
      })
    },
    [dispatch, activeActionGesture, actions],
  )

  const updateAction = useCallback(
    (value: ISetVisibilityAction['option']) => {
      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: actions.map((_a: AnyActionType) => {
          const _action = { ..._a } as SetVisibilityAction
          if (action.id === _action.id) {
            _action.option = value
          }

          return _action
        }),
      })
    },
    [dispatch, activeActionGesture, actions],
  )

  const addNodeIDs = useCallback(
    (values: IOption[]) => {
      const filteredNodes = values.filter(({ value }) => {
        return !action.nodeIDs.includes(value)
      })

      updateNodeIDs([
        ...action.nodeIDs.map((nodeID) => ({
          label: nodeID,
          value: nodeID,
        })),
        ...filteredNodes,
      ])
    },
    [action.nodeIDs, updateNodeIDs],
  )

  const removeNodeIDs = useCallback(
    (values: IOption[]) => {
      const filteredNodes = action.nodeIDs
        .filter((nodeID) => {
          return !values.find(({ value }) => value === nodeID)
        })
        .map((nodeID) => ({
          label: nodeID,
          value: nodeID,
        }))

      updateNodeIDs(filteredNodes)
    },
    [action.nodeIDs, updateNodeIDs],
  )

  return (
    <Box width="100%" display="flex" alignItems="center">
      <HStack spacing="24px" flex="1">
        <MultiSelect
          value={nodesIDs as any}
          width="auto"
          options={nodesOptions}
          onChange={updateNodeIDs as any}
          selectAll={addNodeIDs}
          deselectAll={removeNodeIDs}
        />
        <Select
          value={optionValue}
          width="150px"
          options={OPTIONS}
          isDisabled={isDisabled}
          onChange={(option) => {
            // @ts-ignore
            updateAction(option.value)
          }}
        />
        {/* <Button
          variant="warningGhost"
          fontSize="12px"
          onClick={() => {
            updateNodeIDs([])
          }}
        >
          {'Clear'}
        </Button> */}
      </HStack>
    </Box>
  )
}

export default SetVisibilityActionComponent
